import React from 'react';
import { Box, Button, useTheme, IconButton, Chip, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Edit, Delete, Visibility, VisibilityOff, Add, OpenInNew } from '@mui/icons-material';
import DashboardHeader from '../../components/DashboardHeader/DashboardHeader';
import { useGetPostsQuery, useDeletePostMutation } from '../../state/api';

const BlogAdmin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: posts, isLoading, refetch } = useGetPostsQuery();
  const [deletePost] = useDeletePostMutation();

  const handleDelete = async (id: string) => {
    if (window.confirm('Opravdu chcete smazat tento příspěvek?')) {
      try {
        await deletePost(id).unwrap();
      } catch (error) {
        console.error('Failed to delete post:', error);
      }
    }
  };

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/post/${id}/toggle-publish`, {
        method: 'PATCH',
        credentials: 'include',
      });
      refetch();
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Název',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap="0.5rem">
          {params.row.cover && (
            <Box
              component="img"
              src={`https://smartpush-aws-s3-bucket.s3.eu-central-1.amazonaws.com/${params.row.cover}`}
              alt=""
              sx={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: '4px',
              }}
            />
          )}
          <Box>
            <Box sx={{ fontWeight: 500 }}>{params.value}</Box>
            {params.row.keywords?.length > 0 && (
              <Box display="flex" gap="0.25rem" flexWrap="wrap" mt="0.25rem">
                {params.row.keywords.slice(0, 2).map((keyword: string) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '0.65rem',
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.secondary[200],
                    }}
                  />
                ))}
                {params.row.keywords.length > 2 && (
                  <Chip
                    label={`+${params.row.keywords.length - 2}`}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '0.65rem',
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.secondary[300],
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'isPublished',
      headerName: 'Stav',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Publikováno' : 'Koncept'}
          size="small"
          sx={{
            backgroundColor: params.value ? '#4caf50' : theme.palette.secondary[500],
            color: 'white',
          }}
        />
      ),
    },
    {
      field: 'views',
      headerName: 'Zobrazení',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap="0.25rem">
          <Visibility
            fontSize="small"
            sx={{ color: theme.palette.secondary[300], fontSize: '1rem' }}
          />
          {params.value || 0}
        </Box>
      ),
    },
    {
      field: 'author',
      headerName: 'Autor',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => params.row.author?.email || 'N/A',
    },
    {
      field: 'createdAt',
      headerName: 'Vytvořeno',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        new Date(params.value).toLocaleDateString('cs-CZ'),
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" gap="0.25rem">
          <Tooltip title="Zobrazit na webu">
            <IconButton
              size="small"
              onClick={() => window.open(`/blog/post/${params.row.urlTitle}`, '_blank')}
              sx={{ color: theme.palette.secondary[200] }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isPublished ? 'Skrýt' : 'Publikovat'}>
            <IconButton
              size="small"
              onClick={() => handleTogglePublish(params.row._id, params.row.isPublished)}
              sx={{ color: params.row.isPublished ? '#4caf50' : theme.palette.secondary[300] }}
            >
              {params.row.isPublished ? (
                <Visibility fontSize="small" />
              ) : (
                <VisibilityOff fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Upravit">
            <IconButton
              size="small"
              onClick={() => navigate(`/blog-admin/edit/${params.row.urlTitle}`)}
              sx={{ color: theme.palette.secondary[200] }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Smazat">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row._id)}
              sx={{ color: theme.palette.error.main }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DashboardHeader title="BLOG" subtitle="Správa blogových příspěvků" />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/blog-admin/create')}
          sx={{
            backgroundColor: theme.palette.secondary[300],
            color: theme.palette.primary[600],
            '&:hover': {
              backgroundColor: theme.palette.secondary[100],
            },
          }}
        >
          Nový příspěvek
        </Button>
      </Box>

      {/* Stats row */}
      <Box display="flex" gap="1rem" mt="1.5rem" mb="1rem">
        <Box
          sx={{
            backgroundColor: theme.palette.background.alt,
            p: '1rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#4caf50',
            }}
          />
          <Box sx={{ color: theme.palette.secondary[200] }}>
            Publikováno: {posts?.filter((p: any) => p.isPublished).length || 0}
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: theme.palette.background.alt,
            p: '1rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: theme.palette.secondary[500],
            }}
          />
          <Box sx={{ color: theme.palette.secondary[200] }}>
            Koncepty: {posts?.filter((p: any) => !p.isPublished).length || 0}
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: theme.palette.background.alt,
            p: '1rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Visibility sx={{ color: theme.palette.secondary[300], fontSize: '1rem' }} />
          <Box sx={{ color: theme.palette.secondary[200] }}>
            Celkem zobrazení: {posts?.reduce((acc: number, p: any) => acc + (p.views || 0), 0) || 0}
          </Box>
        </Box>
      </Box>

      <Box
        mt="20px"
        height="70vh"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: theme.palette.primary.light,
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: 'none',
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${theme.palette.secondary[200]} !important`,
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }}
      >
        <DataGrid
          loading={isLoading || !posts}
          getRowId={(row) => row._id}
          rows={posts || []}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
          }}
          disableRowSelectionOnClick
          rowHeight={70}
        />
      </Box>
    </Box>
  );
};

export default BlogAdmin;
